/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Copy, CheckCircle, WhatsappLogo, LinkSimple, Clock } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

// Build an absolute URL from the backend's returned path. We do this on the
// frontend (rather than in the backend) because the frontend knows its own
// origin and the deployment may use CORS_ORIGINS=* which strips the backend's
// ability to infer the parent-facing hostname.
function absoluteUrl(path) {
  return `${window.location.origin}${path}`;
}

export default function InviteParentDialog({ student, open, onClose }) {
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState({ link: false, message: false });

  useEffect(() => {
    if (!open) return;
    setInvite(null);
    setCopied({ link: false, message: false });
    setBusy(true);
    api
      .post("/admin/parent-invites", { student_id: student.id })
      .then(({ data }) => setInvite(data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not generate invite");
        onClose();
      })
      .finally(() => setBusy(false));
  }, [open, student?.id]);

  if (!open) return null;

  const link = invite ? absoluteUrl(invite.path) : "";
  const message = invite ? invite.message_template.replace("{link}", link) : "";
  const whatsappUrl = message
    ? `https://wa.me/?text=${encodeURIComponent(message)}`
    : "";

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((prev) => ({ ...prev, [key]: true }));
      toast.success(key === "link" ? "Link copied" : "Message copied");
      setTimeout(() => setCopied((prev) => ({ ...prev, [key]: false })), 1500);
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };

  const expiresText = invite?.expires_at
    ? new Date(invite.expires_at).toLocaleDateString([], { month: "short", day: "numeric" })
    : "";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        data-testid="invite-parent-overlay"
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
          data-testid="invite-parent-dialog"
        >
          <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-bold tracking-wider uppercase text-slate-500">
                Invite {student.name}'s parent
              </div>
              <h2 className="font-outfit text-2xl font-bold mt-0.5">Send a parent link</h2>
              <p className="text-xs text-slate-500 mt-1">
                Generates a one-time link. When they open it, they set a
                password and get instant access — already linked to {student.name}.
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="invite-parent-close"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          {busy || !invite ? (
            <div className="p-8 text-center text-slate-500" data-testid="invite-parent-loading">
              Generating link...
            </div>
          ) : (
            <div className="overflow-y-auto p-6 space-y-4">
              {/* WhatsApp CTA */}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="invite-parent-whatsapp-btn"
                className="btn-tactile-green w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 text-white font-outfit font-bold text-sm hover:bg-emerald-600"
              >
                <WhatsappLogo size={18} weight="fill" />
                Open in WhatsApp
              </a>

              {/* Copyable link */}
              <div>
                <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-1.5">
                  <LinkSimple size={12} weight="bold" /> Invite link
                </div>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={link}
                    data-testid="invite-parent-link-input"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs text-slate-700 font-mono"
                  />
                  <button
                    onClick={() => copy("link", link)}
                    data-testid="invite-parent-copy-link-btn"
                    className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-1"
                  >
                    {copied.link ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Copy size={16} weight="bold" />}
                    {copied.link ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              {/* Message preview */}
              <div>
                <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-1.5">
                  Ready-to-send message
                </div>
                <textarea
                  readOnly
                  value={message}
                  rows={6}
                  data-testid="invite-parent-message-textarea"
                  onFocus={(e) => e.target.select()}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 leading-relaxed"
                />
                <button
                  onClick={() => copy("message", message)}
                  data-testid="invite-parent-copy-message-btn"
                  className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold hover:bg-slate-50"
                >
                  {copied.message ? <CheckCircle size={14} weight="fill" className="text-emerald-500" /> : <Copy size={14} weight="bold" />}
                  {copied.message ? "Copied" : "Copy message"}
                </button>
              </div>

              <div className="flex items-start gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                <Clock size={14} weight="bold" className="text-slate-400 mt-0.5 shrink-0" />
                <span>
                  This is a <strong>single-use</strong> link that expires on <strong>{expiresText}</strong>.
                  Regenerate anytime if the parent doesn't sign up in time.
                </span>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
